import { useQuery } from "@tanstack/react-query"

/**
 * Projektnummern im Fliesstext anklickbar machen.
 *
 * Der Planer schreibt Projekte als Klartext-Nummer in seine Begruendung
 * („… Projekt KLI-791 passt thematisch") und in Aufgaben-Texte. Wer das liest,
 * will den Vorgang oeffnen — bisher blieb nur, die Nummer zu kopieren und in
 * HERO zu suchen.
 *
 * 🔑 Verlinkt wird nur, was der Projekt-Cache dieses Tenants wirklich kennt.
 * Ein reiner Formtreffer waere zu grob: in denselben Mails stehen
 * Angebotsnummern („VK-SH-2026-1763546"), EU-Verordnungen („2024/2215") und
 * Postleitzahlen. Was der Cache nicht kennt, bleibt Text.
 */

/** Kandidaten-Form: KLI-791, HEZ-733, SAN-1204. Nicht mitten in laengeren
 *  Kennungen — `VK-SH-2026-1763546` darf nicht als „SH-2026" durchgehen. */
const NR_RE = /(^|[^A-Za-z0-9-])([A-Z]{2,5}-\d{1,6})(?![A-Za-z0-9-])/g

export type ProjektLinks = {
  /** Nummer (Grossschreibung) → HERO-Projekt. Nur bestaetigte Treffer. */
  treffer: Record<string, { id: number; title: string }>
  tenant: string
}

export const KEINE_LINKS: ProjektLinks = { treffer: {}, tenant: "" }

/** Alle Kandidaten aus mehreren Texten, dedupliziert. */
export function findeProjektNummern(...texte: (string | null | undefined)[]): string[] {
  const treffer = new Set<string>()
  for (const t of texte) {
    if (!t) continue
    NR_RE.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = NR_RE.exec(t)) !== null) treffer.add(m[2].toUpperCase())
  }
  // Obergrenze wie im Endpoint — mehr als 25 Nummern in einem Plan gibt es nicht.
  return [...treffer].sort().slice(0, 25)
}

/**
 * Loest die Kandidaten gegen den lokalen Projekt-Cache auf (ein Aufruf, kein
 * HERO-Call). Ohne Kandidaten wird nichts angefragt.
 */
export function useProjektNrLinks(
  tenantSlug: string | undefined | null,
  ...texte: (string | null | undefined)[]
): ProjektLinks {
  const nrs = findeProjektNummern(...texte)
  const schluessel = nrs.join(",")
  const q = useQuery({
    queryKey: ["projekt-nr", tenantSlug || "", schluessel],
    enabled: !!tenantSlug && nrs.length > 0,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const r = await fetch(
        `/api/v1/projects/nr-lookup?tenant=${encodeURIComponent(tenantSlug || "")}` +
        `&nrs=${encodeURIComponent(schluessel)}`,
        { credentials: "include" },
      )
      if (!r.ok) return { items: {} }
      return r.json() as Promise<{ items: Record<string, { id: number; title: string }> }>
    },
  })
  return { treffer: q.data?.items || {}, tenant: tenantSlug || "" }
}

/**
 * Text mit verlinkten Projektnummern. Unbekannte Nummern bleiben Text.
 *
 * Der Link geht ueber den Portal-Proxy statt direkt zu HERO: der prueft, ob
 * es den Vorgang noch gibt, und raeumt tote Verknuepfungen auf, statt HEROs
 * 404 zu zeigen.
 */
export function MitProjektLinks({ text, links }: { text: string; links: ProjektLinks }) {
  if (!text) return null
  const bekannt = Object.keys(links.treffer).length > 0
  if (!bekannt) return <>{text}</>

  const teile: React.ReactNode[] = []
  let zuletzt = 0
  let key = 0
  NR_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = NR_RE.exec(text)) !== null) {
    const nr = m[2]
    const projekt = links.treffer[nr.toUpperCase()]
    if (!projekt) continue
    const start = m.index + m[1].length
    if (start > zuletzt) teile.push(text.slice(zuletzt, start))
    teile.push(
      <a
        key={key++}
        href={`/admin/open-hero/project/${projekt.id}?tenant=${encodeURIComponent(links.tenant)}`}
        target="_blank"
        rel="noreferrer"
        title={projekt.title ? `${projekt.title} — in HERO öffnen` : "In HERO öffnen"}
        style={{ color: "#4f46e5", textDecoration: "underline", textUnderlineOffset: 2 }}
      >
        {nr}
      </a>,
    )
    zuletzt = start + nr.length
  }
  if (zuletzt === 0) return <>{text}</>
  if (zuletzt < text.length) teile.push(text.slice(zuletzt))
  return <>{teile}</>
}
